/**
 * Wrap a bare expression template (e.g. `concat('a', 'b')`) in the runtime's
 * `${...}` evaluator syntax so it parses as an expression at evaluation time
 * instead of as a literal string.
 *
 * Three insertion paths in the workflow designer use this primitive:
 *   1. The field-fork popover's insert button (FieldForkContainer)
 *   2. The rail's Expressions tab insert (nodeEditorStore.insertExpressionToParameter)
 *   3. Drag-and-drop into a parameter field — but only when the drop point
 *      is *outside* an existing `${...}` expression (TokenizedInput.handleDrop).
 *
 * Path 3 is context-sensitive; bare templates dropped *inside* an existing
 * expression are pasted as-is. The context-sensitivity stays at the call site;
 * this helper is just the leaf wrapper.
 */
export function wrapAsExpression(template: string): string {
  return `\${${template}}`;
}

/**
 * Returns true when the text contains at least one `${...}` workflow
 * template token. Used by structured editors (array, dictionary) to
 * bypass JSON validation — the token resolves at runtime and may itself
 * be an array or object, so the editor can't statically validate the
 * shape and must pass the raw expression through.
 */
export function containsTemplateToken(text: string): boolean {
  return /\$\{[^}]*\}/.test(text);
}

/**
 * Build a single JS optional bracket accessor: `?.['key']`.
 *
 * Must include the dot — `?[key]` is NOT valid JS optional chaining
 * and the runtime will reject it. Keep this helper as the single source
 * of truth for the accessor shape across token / manifest emitters.
 *
 * The key is escaped for embedding inside a single-quoted JS string so
 * keys containing `\` or `'` (legal in JSON property names) produce
 * valid JS rather than syntax errors or accidental string termination.
 */
export function optionalBracketAccessor(key: string): string {
  const escaped = key.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
  return `?.['${escaped}']`;
}

/**
 * Convert a dotted property path (e.g. `"a.b.c"`) into chained optional
 * bracket accessors (e.g. `"?.['a']?.['b']?.['c']"`). Empty input yields
 * an empty string. Each segment is wrapped via {@link optionalBracketAccessor}.
 */
export function pathToOptionalAccessors(path: string): string {
  if (!path) return '';
  return path.split('.').map(optionalBracketAccessor).join('');
}
